const pageResults = require('graph-results-pager');

const { request, gql } = require('graphql-request');

const { graphAPIEndpoints, topdogAddress, TWENTY_FOUR_HOURS, REWARD_POOL_IDS } = require('./../constants')
const { timestampToBlock, getAverageBlockTime } = require('./../utils');

const { pairs: exchangePairs } = require('./exchange');
const { priceUSD: bonePriceUSD } = require('./bone');

module.exports = {
    async info({block = undefined, timestamp = undefined} = {}) {
        block = block ? block : timestamp ? (await timestampToBlock(timestamp)) : undefined;
        block = block ? `block: { number: ${block} }` : "";

        const result = await request(graphAPIEndpoints.topdog,
            gql`{
                    topDogs(id: "${topdogAddress}", ${block}) {
                        ${info.properties.toString()}
                    }
                }`
        );

        return info.callback(result.topDog);
    },

    async pool({block = undefined, timestamp = undefined, pool_id = undefined, pool_address = undefined} = {}) {
        if(!pool_id && !pool_address) { throw new Error("shibaswap-data: Pool ID / Address undefined"); }

        block = block ? block : timestamp ? (await timestampToBlock(timestamp)) : undefined;
        block = block ? `block: { number: ${block} }` : "";

        let result;
        if(pool_id) {
            result = await request(graphAPIEndpoints.topdog,
                gql`{
                        pool(id: ${pool_id}, ${block}) {
                            ${pools.properties.toString()}
                        }
                    }`
            );
        }

        else {
            result = await request(graphAPIEndpoints.topdog,
                gql`{
                        pools(first: 1, where: {pair: "${pool_address.toLowerCase()}"}, ${block}) {
                            ${pools.properties.toString()}
                        }
                    }`
            );
        }

        return pools.callback(pool_id ? [result.pool] : result.pools)[0];
    },

    async pools({block = undefined, timestamp = undefined} = {}) {
        return pageResults({
            api: graphAPIEndpoints.topdog,
            query: {
                entity: 'pools',
                selection: {
                    block: block ? { number: block } : timestamp ? { number: await timestampToBlock(timestamp) } : undefined,
                },
                properties: pools.properties
            }
        })
            .then(results => pools.callback(results))
            .catch(err => console.log(err));
    },

    async stakedValue({block = undefined, timestamp = undefined, token_address = undefined} = {}) {
        if(!token_address) { throw new Error("shibaswap-data: Token address undefined"); }

        block = block ? block : timestamp ? (await timestampToBlock(timestamp)) : undefined;
        block = block ? `block: { number: ${block} }` : "";

        const result = await request(graphAPIEndpoints.exchange,
            gql`{
                    liquidityPosition(id: "${token_address.toLowerCase()}-${topdogAddress}", ${block}) {
                        ${stakedValue.properties.toString()}
                    }
                }`
        );

        return stakedValue.callback(result.liquidityPosition);
    },

    async user({block = undefined, timestamp = undefined, user_address = undefined} = {}) {
        if(!user_address) { throw new Error("shibaswap-data: User address undefined"); }

        return pageResults({
            api: graphAPIEndpoints.topdog,
            query: {
                entity: 'users',
                selection: {
                    where: {
                        address: `\\"${user_address.toLowerCase()}\\"`
                    },
                    block: block ? { number: block } : timestamp ? { number: await timestampToBlock(timestamp) } : undefined,
                },
                properties: user.properties
            }
        })
            .then(results => user.callback(results))
            .catch(err => console.log(err));
    },

    async users({block = undefined, timestamp = undefined} = {}) {
        return pageResults({
            api: graphAPIEndpoints.topdog,
            query: {
                entity: 'users',
                selection: {
                    block: block ? { number: block } : timestamp ? { number: await timestampToBlock(timestamp) } : undefined,
                },
                properties: user.properties
            }
        })
            .then(results => user.callback(results))
            .catch(err => console.log(err));
    },

    async apys({block = undefined, timestamp = undefined} = {}) {
        const topdogList = await module.exports.pools({block, timestamp});
        const exchangeList = await exchangePairs({block, timestamp});
        const boneUSD = await bonePriceUSD({block, timestamp});

        const totalAllocPoint = topdogList.reduce((a, b) => a + b.allocPoint, 0);

        const averageBlockTime = await getAverageBlockTime({block, timestamp});

        return topdogList.map(topdogPool => {
            const exchangePool = exchangeList.find(e => e.id === topdogPool.pair);
            if(!exchangePool) {
                return {...topdogPool, apy: 0};
            }

            const tvl = topdogPool.sslpBalance * (exchangePool.reserveUSD / exchangePool.totalSupply);
            const bonePerBlock = (topdogPool.allocPoint / (totalAllocPoint) * 100);
            const apy = boneUSD * (bonePerBlock * (60 / averageBlockTime) * 60 * 24 * 365) / tvl * 100 * 3; // *3 => vesting

            return {...topdogPool, apy};
        });
    },

    async apys24h({block = undefined, timestamp = undefined} = {}) {
        let timestampNow = timestamp ? timestamp : block ? await blockToTimestamp(block) : (Math.floor(Date.now() / 1000));
        timestamp24ago = timestampNow - TWENTY_FOUR_HOURS;
        timestamp48ago = timestamp24ago - TWENTY_FOUR_HOURS;

        block = timestamp ? await timestampToBlock(timestamp) : block;
        block24ago = await timestampToBlock(timestamp24ago);
        block48ago = await timestampToBlock(timestamp48ago);

        const results = await module.exports.apys({block: block});
        const results24ago = await module.exports.apys({block: block24ago});

        return apys.callback24h(results, results24ago);
    },

    async rewardPools({block = undefined, timestamp = undefined} = {}) {
        return pageResults({
            api: graphAPIEndpoints.topdog,
            query: {
                entity: 'pools',
                selection: {
                    block: block ? { number: block } : timestamp ? { number: await timestampToBlock(timestamp) } : undefined,
                    where: {
                        id_in : REWARD_POOL_IDS
                    }
                },
                properties: rewardPools.properties
            }
        })
            .then(results => rewardPools.callback(results))
            .catch(err => console.log(err));
    },

    async rewardPool({block = undefined, timestamp = undefined, pool_id = undefined, last_id = ""} = {}) {
        block = block ? block : timestamp ? (await timestampToBlock(timestamp)) : undefined;
        block = block ? `block: { number: ${block} }` : "";

        let result = await request(graphAPIEndpoints.topdog,
            gql`{
                pool(id: ${pool_id}, ${block}) {
                    id
                    sslpBalance
                    balance
                    userCount
                    users (first : 1000 , where : {id_gt : "${last_id}"}) {
                        id
                        address
                        amount
                    }
                }
            }`
        );

        return rewardPool.callback(result.pool);
    },
}

const info = {
    properties: [
        'bonusMultiplier',
        'bonusEndBlock',
        'devaddr',
        'migrator',
        'owner',
        'startBlock',
        'bone',
        'bonePerBlock',
        'totalAllocPoint',
        'poolCount',
        'sslpBalance',
        'sslpAge',
        'sslpAgeRemoved',
        'sslpDeposited',
        'sslpWithdrawn',
        'updatedAt'
    ],

    callback(results) {
        return ({
            bonusMultiplier: Number(results.bonusMultiplier),
            bonusEndBlock: Number(results.bonusEndBlock),
            devaddr: results.devaddr,
            migrator: results.migrator,
            owner: results.owner,
            startBlock: Number(results.startBlock),
            bonePerBlock: results.bonePerBlock / 1e18,
            totalAllocPoint: Number(results.totalAllocPoint),
            poolCount: Number(results.poolCount),
            sslpBalance: Number(results.sslpBalance),
            sslpAge: Number(results.sslpAge),
            sslpAgeRemoved: Number(results.sslpAgeRemoved),
            sslpDeposited: Number(results.sslpDeposited),
            sslpWithdrawn: Number(results.sslpWithdrawn),
            updatedAt: Number(results.updatedAt)
        });
    }
};

const pools = {
    properties: [
        'id',
        'pair',
        'allocPoint',
        'lastRewardBlock',
        'accBonePerShare',
        'balance',
        'userCount',
        'sslpBalance',
        'sslpAge',
        'sslpAgeRemoved',
        'sslpDeposited',
        'sslpWithdrawn',
        'timestamp',
        'block',
        'updatedAt',
        'entryUSD',
        'exitUSD',
        'boneHarvested',
        'boneHarvestedUSD',
        'users { id, address, amount }'
    ],

    callback(results) {
        return results.map(({ id, pair, allocPoint, lastRewardBlock, accBonePerShare, balance, userCount, sslpBalance, sslpAge, sslpAgeRemoved, sslpDeposited, sslpWithdrawn, timestamp, block, updatedAt, entryUSD, exitUSD, boneHarvested, boneHarvestedUSD, users }) => ({
            id: Number(id),
            pair: pair,
            allocPoint: Number(allocPoint),
            lastRewardBlock: Number(lastRewardBlock),
            accBonePerShare: BigInt(accBonePerShare),
            userCount: Number(userCount),
            sslpBalance: Number(sslpBalance),
            sslpAge: Number(sslpAge),
            sslpAgeRemoved: Number(sslpAgeRemoved),
            sslpDeposited: Number(sslpDeposited),
            sslpWithdrawn: Number(sslpWithdrawn),
            addedTs: Number(timestamp),
            addedDate: new Date(timestamp * 1000),
            addedBlock: Number(block),
            lastUpdatedTs: Number(updatedAt),
            lastUpdatedDate: new Date(updatedAt * 1000),
            entryUSD: Number(entryUSD),
            exitUSD: Number(exitUSD),
            boneHarvested: Number(boneHarvested),
            boneHarvestedUSD: Number(boneHarvestedUSD),
            balance : Number(balance),
            users: users ? users.map(eachUser => {
                return {
                    id : eachUser.id || "",
                    address : eachUser.address || "",
                    amount : Number(eachUser.amount) || Number("0")
                }
            }) : [{
                id  : "",
                address : "",
                amount : Number("0")
            }]
         }));
    }
};

const stakedValue = {
    properties: [
        'id',
        'liquidityTokenBalance',
        'pair { id, totalSupply, reserveETH, reserveUSD }'
    ],

    callback(results) {
        return ({
            id: results.id,
            liquidityTokenBalance: Number(results.liquidityTokenBalance),
            totalSupply: Number(results.pair.totalSupply),
            totalValueETH: Number(results.pair.reserveETH),
            totalValueUSD: Number(results.pair.reserveUSD)
        })
    }
};

const user = {
    properties: [
        'id',
        'address',
        'pool { id, pair, balance, accBonePerShare, lastRewardBlock }',
        'amount',
        'rewardDebt',
        'entryUSD',
        'exitUSD',
        'boneHarvested',
        'boneHarvestedUSD',
    ],

    callback(results) {
        return results.map(entry => ({
            id: entry.id,
            address: entry.address,
            poolId: Number(entry.id.split("-")[0]),
            pool: entry.pool ? {
                id: entry.pool.id,
                pair: entry.pool.pair,
                balance: Number(entry.pool.balance),
                accBonePerShare: Number(entry.pool.accBonePerShare),
                lastRewardBlock: Number(entry.pool.lastRewardBlock)
            } : undefined,
            amount: Number(entry.amount),
            rewardDebt: BigInt(entry.rewardDebt),
            entryUSD: Number(entry.entryUSD),
            exitUSD: Number(entry.exitUSD),
            boneHarvested: Number(entry.boneHarvested),
            boneHarvestedUSD: Number(entry.boneHarvestedUSD),
        }));
    }
};

const apys = {
    callback24h(results, results24h) {
        return results.map(result => {
            const result24h = results24h.find(e => e.id === result.id) || result;

            return ({
                ...result,

                sslpBalanceChange: (result.sslpBalance / result24h.sslpBalance) * 100 - 100,
                sslpBalanceChangeCount: result.sslpBalance - result24h.sslpBalance,

                userCountChange: (result.userCount / result24h.userCount) * 100 - 100,
                userCountChangeCount: result.userCount - result24h.userCount,

                boneHarvestedChange: (result.boneHarvested / result24h.boneHarvested) * 100 - 100,
                boneHarvestedChangeCount: result.boneHarvested - result24h.boneHarvested,
            });
        });
    }
};

const rewardPools = {
    properties: [
        'id',
        'pair',
        'allocPoint',
        'balance',
        'sslpBalance',
        'userCount',
        'users(first :1000 , where : {id_gte :"0-0xfffe3bc6af7dd4451c4f92035134c6d13c8b56b7") { id, address, amount }'
    ],

    callback(results) {
        return results.map(({ id, pair, allocPoint, balance, sslpBalance, users}) => ({
            id: Number(id),
            pair: pair,
            allocPoint: Number(allocPoint),
            balance: Number(balance),
            sslpBalance: Number(sslpBalance),
            users: users ? {
                id : users.id,
                address : users.address,
                amount : Number(users.amount)
            } : {
                id : "",
                address: "",
                amount: 0
            }
        }));
    }
};

// const poolUsers = {
//     properties: [
//         'id',
//         'pair',
//         'allocPoint',
//         'balance',
//         'sslpBalance',
//         'users { id, address, amount }'
//     ],
//
//     callback(results) {
//         return results.map(({ id, pair, allocPoint, balance, sslpBalance, users}) => ({
//             id: Number(id),
//             pair: pair,
//             allocPoint: Number(allocPoint),
//             balance: Number(balance),
//             sslpBalance: Number(sslpBalance),
//             users: users ? {
//                 id : users.id,
//                 address : users.address,
//                 amount : Number(users.amount)
//             } : {
//                 id : "",
//                 address: "",
//                 amount: 0
//             }
//         }));
//     }
// };

const rewardPool = {
    callback(pool) {
        return {
            id: Number(pool.id),
            balance: Number(pool.balance),
            sslpBalance: Number(pool.sslpBalance),
            userCount : Number(pool.userCount),
            users: pool.users ? pool.users.map(eachUser => {
                return {
                    id : eachUser.id,
                    address : eachUser.address,
                    amount : Number(eachUser.amount)
                }
            }) : []
        };
    }
}